import { Component, Inject, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { faFileImage } from "@fortawesome/free-regular-svg-icons";
import { SessionService } from "src/app/session.service";
import moment from "moment";
import { UtilService } from "src/app/util/util.service";
import { find, get } from "lodash";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { CustomersService } from "../customers.service";
import { cloneDeep } from "lodash";
import { sortBy } from "lodash";

@Component({
  selector: "app-customer",
  templateUrl: "./customer.component.html",
  styleUrls: ["./customer.component.scss"],
})
export class CustomerComponent implements OnInit {
  customer: any;
  faFileImage = faFileImage;
  devices!: any[];
  communicationHistory!: { date: string; type: string; sentTime: string }[];
  locationHistory!: { date: string; area: string; difference: string }[];
  locationPermission!: string | null;
  bluetoothPermission!: string | null;
  notificationPermission!: string | null;
  profileFields: any;
  customFields: any;
  customerName: any;
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<CustomerComponent>,
    private route: ActivatedRoute,
    private SessionService: SessionService,
    private Router: Router,
    private utilSvc: UtilService,
    private custSvc: CustomersService,
  ) {}

  ngOnInit(): void {
    
    this.customer = this.route.snapshot.data.customer || this.data.customer;
    this.devices = this.getAppDevices();
    this.communicationHistory = this.getCommunicationHistory(
      this.route.snapshot.data.signalHistory || this.data.signalHistory,
    );
    this.locationHistory = this.getLocationHistory(
      this.route.snapshot.data.locationHistory || this.data.locationHistory,
    );

    this.profileFields =
      this.currentAccount?.catalog.app_users?.profile?.filter(
        (item: any) =>
          item.customer_detail !== false && item.type !== "ImageURL" && item.type !== "CloudinaryImage",
      );

    this.initCustomFields();

    this.customerName =
      this.customer.first_name && this.customer.last_name
        ? `${this.customer.first_name} ${this.customer.last_name}`
        : "No customer name";
  }

  get currentAccount() {
    return this.SessionService.currentAccountValue;
  }

  getAppDevices() {
    const locPermissions: any = {
      all: "All",
      unknown: "Unknown",
      always: "Always Allowed",
      partial: "When In App",
      denied: "Denied",
    };

    const locPermission: any = {
      0: "Unknown",
      1: "Restricted",
      2: "Denied",
      3: "Always",
      4: "When in use",
    }

    const bluetoothPermissions: any = {
      all: "All",
      unknown: "Unknown",
      granted: "Granted",
      denied: "Denied",
    };

    return sortBy(this.customer.appDevices, 'created_at').map((appDevice: any) => {
      const make = appDevice.device.make;
      const model = appDevice.device.model;
      const systemVersion = appDevice.device.system_version;

      return {
        appVersion: appDevice.app_version,
        name: `${make} ${model} ${
          systemVersion ? "(" + systemVersion + ")" : ""
        }`,
        locationPermission: locPermission[appDevice.location_permission],
        bluetoothPermission: bluetoothPermissions[appDevice.allows_btle]
          ? "Yes"
          : "No",
        notificationPermission: appDevice.allows_push ? "Yes" : "No",
        createdAt: appDevice.created_at,
      };
    });
  }

  getCommunicationHistory(signalHistory: any[]) {
    return signalHistory?.map((signal) => {
      return {
        date: this.utilSvc.formatDate(signal.created_at, "DD MMM yyyy"),
        type: "QUICK SIGNAL",
        sentTime: this.utilSvc.formatDate(signal.created_at, "HH:mm z"),
      };
    });
  }

  getLocationHistory(locationHistory: any[]) {
    return locationHistory.map((location) => {
      return {
        date: this.utilSvc.formatDate(location.created_at, "DD MMM yyyy"),
        difference: this.getLocationHistoryDifference(
          location.entry_at,
          location.exit_at,
        ),
        area: this.getLocationName(location),
      };
    });
  }

  getDate(date: Date) {
    const weekdays = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const dayOfWeek = weekdays[date.getDay()];
    const month = months[date.getMonth()];
    const dayOfMonth = date.getDate();
    const year = date.getFullYear();

    return `${dayOfWeek}, ${month} ${dayOfMonth}, ${year}`;
  }

  getCommSentTime(date: Date) {
    return `${this.getTime(date)} - ${this.getDate(date)}`;
  }

  getTime(date: Date) {
    let isPM = false;
    let hour = date.getHours();
    if (hour >= 12) {
      isPM = true;
      if (hour > 12) {
        hour = hour - 12;
      }
    }

    let minutes = date.getMinutes().toString();
    if (minutes.length < 2) {
      minutes = `0${minutes}`;
    }

    return `${hour}:${minutes} ${isPM ? "PM" : "AM"}`;
  }

  getLocationHistoryDifference(startDate: Date, endDate: Date) {
    startDate = new Date(startDate);

    const stillThere = !endDate;

    if (stillThere) {
      endDate = new Date();
    } else {
      endDate = new Date(endDate);
    }

    let timeDiff = endDate.getTime() - startDate.getTime();
    const dayDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    if (dayDiff) {
      timeDiff = timeDiff - dayDiff * (1000 * 60 * 60 * 24);
    }
    const hourDiff = Math.floor(timeDiff / (1000 * 60 * 60));
    if (hourDiff) {
      timeDiff = timeDiff - hourDiff * (1000 * 60 * 60);
    }
    const minuteDiff = Math.floor(timeDiff / (1000 * 60));
    if (minuteDiff) {
      timeDiff = timeDiff - minuteDiff * (1000 * 60);
    }
    const secondDiff = Math.floor(timeDiff / 1000);

    const timeDiffString = `${dayDiff ? dayDiff + "d " : ""}${
      hourDiff ? hourDiff + "h " : ""
    }${minuteDiff ? minuteDiff + "m " : ""}${
      secondDiff ? secondDiff + "s " : ""
    }`;

    return (
      `From ${this.formatDate(startDate, "HH:mm z")}` +
      ` ${
        !stillThere
          ? " To " + this.formatDate(endDate, "HH:mm z")
          : ""
      } (${timeDiffString.trim()})`
    );
  }

  getLocationName(location: any) {
    return location.beaconRegion?.name
      ? location.beaconRegion?.name.toUpperCase()
      : location.geofence?.name?.toUpperCase();
  }

  initCustomFields() {
    const userProfile = this.customer.profile;

    this.customFields = this.profileFields?.map((field: any) => {

      let value = null;

      if(field.field.startsWith("appUser.profile.")){
        value = userProfile[field.field.replace("appUser.profile.", "")]
      } else {
        value = userProfile[field.id];
      }

      if (field.type === "String[]") {
        if (!value) {
          field.value = value.join(", ");
        }
      } else if (field.type === "Integer[]") {
        if (!value) {
          const val = value.map((intVal: string) => parseInt(intVal));
          field.value = val.join(", ");
        }
      } else if (field.type === "Float[]") {
        if (!value) {
          field.value = value.join(", ");
        }
      } else if (field.type === "Integer") {
        if (!value) {
          field.value = parseInt(value, 10);
        }
      } else if (field.type === "Float") {
        if (!value) {
          field.value = parseInt(value, 10);
        }
      } else {
        field.value = value;
      }

      return field;
    });

  }

  formatDate = (date: Date, format?: string) => {
    return this.utilSvc.formatDate(date, format);
  }

  age() {
    return "(" + moment().diff(moment(this.customer.date_of_birth), "years").toString() + ")";
  }

  customerImageStyle() {
    if(this.image() !== null){
      return `background-image: url(${this.image()}); background-size: cover; background-position: center; cursor: pointer; aspect-ratio: 1;`
    }
    return ""
  }

  image(){
    if(this.customer.profile === undefined || this.customer.profile === null){
      return null
    }
    let cloudinaryImage = find(this.currentAccount?.catalog.app_users?.profile, {id: "image", _type: "CloudinaryImage"})
    if(this.utilSvc.isValidHttpUrl(this.customer.profile.image)){
      return this.customer.profile.image
    } else if (cloudinaryImage !== undefined) {
      let imagePath = get(this.customer, cloudinaryImage.field.replace("appUser.", ""))
      if(imagePath === "" || imagePath === undefined || imagePath === null){
        return null;
      }
      return `https://res.cloudinary.com/${cloudinaryImage.cloud_name}/image/upload/${imagePath}`
    }
    return null
  }

  openImage(){
    if(this.image() !== null){
      window.open(this.image(), "_blank")
    }
  }

  sendPrivateBroadcast() {
    this.dialogRef.close('sendPrivateBroadcast');
  }
}
